import { useState, useEffect, useCallback } from 'react'
import type { GeoApiKey } from '../types'

export function useGeoKeys() {
  const [keys, setKeys] = useState<GeoApiKey[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchKeys = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await fetch('/api/geo-keys')
      if (!response.ok) throw new Error('Failed to fetch geo API keys')
      const data = await response.json()
      setKeys(data.keys || [])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchKeys()
  }, [fetchKeys])

  const createKey = async (label: string): Promise<{ rawKey: string; id: number }> => {
    const res = await fetch('/api/geo-keys', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ label }),
    })
    if (!res.ok) {
      const data = await res.json()
      throw new Error(data.error || 'Failed to create key')
    }
    const data = await res.json()
    await fetchKeys()
    return data
  }

  const revokeKey = async (id: number): Promise<void> => {
    const res = await fetch(`/api/geo-keys/${id}/revoke`, { method: 'POST' })
    if (!res.ok) {
      const data = await res.json()
      throw new Error(data.error || 'Failed to revoke key')
    }
    await fetchKeys()
  }

  return { keys, loading, error, refetch: fetchKeys, createKey, revokeKey }
}
